import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  useFlag,
  useFlags,
  usePassActiveOrder,
  usePassCancelOrder,
  usePassChances,
  usePassDecks,
  usePassOrder,
  usePassOrderInvoice,
  usePassPrizes,
  usePassShop,
  usePass,
} from '../api/hooks';
import type { ShopEntry } from '../api/types';
import { plural } from '../format';
import { ErrorView, Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { SheetClose } from '../components/SheetClose';
import { DeckBox } from '../components/pass/DeckBox';
import { RarityBadge } from '../components/pass/RarityBadge';
import { SlotGlyph } from '../components/pass/RewardPreview';
import {
  RARITY_COLOR,
  RARITY_LABEL,
  RARITY_LABEL_M,
  rarityTextStyle,
  rarityTint,
} from '../components/cosmetics/cosmeticsMap';
import { backButton, canPayStars, haptics, openInvoice } from '../telegram';
import { SnowIcon } from './PassScreen';
import { PayMethods, useStarsOrder } from '../components/pass/payments';
import { track } from '../analytics';

const RKEY = ['common', 'rare', 'epic', 'legendary'] as const;

/** Цена заказа в ⭐ (зеркало orderStarsPrice в API); null — Stars недоступны. */
function starsTotal(entry: ShopEntry, qty: number): number | null {
  if (!entry.starsPrice) return null;
  if (entry.bulkQty && entry.starsBulkPrice && qty >= entry.bulkQty) {
    return Math.max(1, Math.round((entry.starsBulkPrice / entry.bulkQty) * qty));
  }
  return entry.starsPrice * qty;
}

/** ×N-группировка инвентаря: редкость + источник. Колоды с дорожки Pass+
 *  (track) называются игровыми (решение владельца), шансы у них купленной. */
function groupDecks(list: { id: number; rarity: number; source: string }[]) {
  const groups = new Map<string, { rarity: number; bought: boolean; count: number; firstId: number }>();
  for (const d of list) {
    const bought = d.source === 'bought';
    const key = `${d.rarity}-${bought}`;
    const g = groups.get(key);
    if (g) g.count++;
    else groups.set(key, { rarity: d.rarity, bought, count: 1, firstId: d.id });
  }
  return [...groups.values()].sort((a, b) => b.rarity - a.rarity);
}

function SourceIcon({ bought }: { bought: boolean }) {
  return bought ? (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <path d="M6 7h12l-1.5 12h-9L6 7zM9 7a3 3 0 0 1 6 0" />
    </svg>
  ) : (
    // игровая — карта (макет 6.4)
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <rect x="5" y="3" width="14" height="18" rx="2.5" />
      <path d="M9.5 10.5c1.3-1.7 5-1.7 5 0s-3.7 1.7-5 0z" opacity=".7" />
    </svg>
  );
}

const FREEZE_SHORT: Record<string, string> = {
  game1: '1 игра',
  game3: '3 игры',
  week: 'неделя',
  eternal: 'вечная',
};

/** 6.4 инвентарь колод + 6.10 магазин (шторки qty/призы) + 6.6 шансы. */
export function DecksScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const decks = usePassDecks(enabled);
  const pass = usePass(enabled);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<number | null>(null);
  // ?shop=1 (кнопка «Магазин» на главной) — шторка магазина сразу открыта
  const [sheet, setSheet] = useState<null | 'shop' | 'chances'>(() =>
    new URLSearchParams(window.location.search).get('shop') ? 'shop' : null,
  );
  const [qtyRarity, setQtyRarity] = useState<number | null>(null);
  const [prizesRarity, setPrizesRarity] = useState<number | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);

  // Тост «Надето · {имя}» после «Надеть» на 6.5b (handoff 4).
  const location = useLocation();
  const [toast, setToast] = useState<string | null>(() => {
    const fromState = (location.state as { toast?: string } | null)?.toast;
    if (fromState) return fromState;
    // 6.5b выходит через navigate(-1) (state не передать) — тост едет так
    try {
      const v = sessionStorage.getItem('bp_equip_toast');
      if (v) {
        sessionStorage.removeItem('bp_equip_toast');
        return v;
      }
    } catch {
      /* private mode */
    }
    return null;
  });
  useEffect(() => {
    if (!toast) return;
    window.history.replaceState({}, '');
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    // -1, не push «/pass»: push зацикливал историю (пасс → назад → снова
    // колоды). /open-экраны выходят через -1 и в истории не остаются.
    const back = () => {
      if ((window.history.state as { idx?: number } | null)?.idx) navigate(-1);
      else navigate('/pass', { replace: true });
    };
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data) return <Loader />;
  if (decks.isLoading) return <Loader />;
  if (decks.isError || !decks.data) return <ErrorView message={(decks.error as Error)?.message} />;

  const list = decks.data.decks.filter((d) => filter === null || d.rarity === filter);

  return (
    <div className="bp-screen">
      <div className="subhead">
        <button
          className="iconbtn"
          onClick={() =>
            (window.history.state as { idx?: number } | null)?.idx
              ? navigate(-1)
              : navigate('/pass', { replace: true })
          }
          aria-label="Назад"
        >
          <BackIcon />
        </button>
        <div className="title">
          Колоды <span className="bp-title-count mono">{decks.data.decks.length}</span>
        </div>
      </div>

      {/* чипы фильтра по макету 6.4: короткие подписи + счётчики */}
      <div className="bp-deck-filters">
        <button className={`bp-deck-filter${filter === null ? ' on' : ''}`} onClick={() => setFilter(null)}>
          Все <b className="mono">{decks.data.decks.length}</b>
        </button>
        {[3, 2, 1, 0].map((r) => {
          const n = decks.data.decks.filter((d) => d.rarity === r).length;
          if (n === 0) return null;
          return (
            <button
              key={r}
              className={`bp-deck-filter${filter === r ? ' on' : ' rtx'}`}
              style={filter === r ? undefined : rarityTextStyle(r)}
              onClick={() => setFilter(r)}
            >
              {['Обычные', 'Редкие', 'Эпик', 'Легенд.'][r]} <b className="mono">{n}</b>
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <div className="q-empty">
          Неоткрытых колод нет — колода выдаётся за каждую сыгранную игру сезона.
        </div>
      ) : (
        <div className="bp-decks-grid">
          {/* ×N: одинаковые (редкость + источник) колоды одной карточкой.
              Колоды с дорожки считаются купленными (решение владельца). */}
          {groupDecks(list).map((g) => (
            <button
              key={`${g.rarity}-${g.bought}`}
              className="bp-deck-card"
              style={{ borderColor: g.rarity >= 2 ? rarityTint(g.rarity, 0.55) : undefined }}
              onClick={() => {
                haptics.tap();
                navigate(`/pass/decks/${g.firstId}/open`);
              }}
            >
              <span className="bp-deck-wrap">
                <DeckBox rarity={g.rarity} stage="idle" size={40} />
                {g.count > 1 && <span className="bp-deck-count mono">×{g.count}</span>}
              </span>
              <span className="nm rtx" style={rarityTextStyle(g.rarity)}>
                {RARITY_LABEL[g.rarity]}
              </span>
              <span className="src">
                <SourceIcon bought={g.bought} />
                {g.bought ? 'Купленная' : 'Игровая'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* макет: плашка с часами и числом дней */}
      <div className="bp-decks-note">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.5V12l3 2" />
        </svg>
        Неоткрытые колоды откроются автоматически в конце сезона
        {pass.data?.season ? ` — через ${pass.data.season.daysLeft} ${plural(pass.data.season.daysLeft, ['день', 'дня', 'дней'])}.` : '.'}
      </div>

      <div className="bp-decks-actions">
        {decks.data.decks.length > 0 && (
          <button
            className="btn"
            style={{ flex: 1.4 }}
            onClick={() => {
              const first = decks.data.decks[0];
              if (first) navigate(`/pass/decks/${first.id}/open?all=1`);
            }}
          >
            Открыть все <b className="bp-btn-count mono">{decks.data.decks.length}</b>
          </button>
        )}
        <button className="btn-secondary bp-btn-secondary" onClick={() => setSheet('shop')}>
          Купить ещё
        </button>
      </div>
      <button className="bp-chances-link" onClick={() => setSheet('chances')}>
        Шансы выпадений
      </button>

      {sheet === 'shop' && (
        <ShopSheet
          onClose={() => setSheet(null)}
          onQty={(r) => setQtyRarity(r)}
          onPrizes={(r) => setPrizesRarity(r)}
          onChances={() => setSheet('chances')}
        />
      )}
      {qtyRarity !== null && (
        <QtySheet
          rarity={qtyRarity}
          onClose={() => setQtyRarity(null)}
          onOrdered={() => {
            setQtyRarity(null);
            setSheet(null);
            setOrderOpen(true);
          }}
        />
      )}
      {prizesRarity !== null && (
        <PrizesSheet rarity={prizesRarity} onClose={() => setPrizesRarity(null)} />
      )}
      {sheet === 'chances' && <ChancesSheet onClose={() => setSheet(null)} />}
      {orderOpen && <OrderSheet onClose={() => setOrderOpen(false)} />}
      {toast && <div className="bp-equip-toast">{toast}</div>}
    </div>
  );
}

/* ───────────────────────── 6.10a магазин ───────────────────────── */

function ShopSheet({
  onClose,
  onQty,
  onPrizes,
  onChances,
}: {
  onClose: () => void;
  onQty: (rarity: number) => void;
  onPrizes: (rarity: number) => void;
  onChances: () => void;
}) {
  const shop = usePassShop();
  const decks = usePassDecks(true);
  const starsAllowed = useFlag('pass_stars') && canPayStars;
  if (!shop.data) return null;
  const mine = decks.data?.decks.length ?? 0;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-shop" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <div className="bp-shop-head">
          <div>
            <h3>Магазин колод</h3>
            <div className="sheet-sub">предметы гардероба и заморозки</div>
          </div>
          {mine > 0 && (
            <span className="bp-shop-mine">
              Мои <b className="mono">{mine}</b>
            </span>
          )}
        </div>
        {[1, 2, 3].map((r) => {
          const entry = shop.data.shop[RKEY[r]];
          if (!entry) return null;
          const perBulk = entry.bulkQty && entry.bulkPrice ? Math.round(entry.bulkPrice / entry.bulkQty) : null;
          const starsOne = starsAllowed ? starsTotal(entry, 1) : null;
          const drops = (shop.data.previews[RKEY[r]] ?? []).slice(0, 4);
          return (
            <div key={r} className="bp-shop-card">
              <span className="bp-shop-halo" style={{ background: `radial-gradient(circle, ${rarityTint(r, 0.28)}, transparent 70%)` }} />
              <button className="bp-shop-deck" onClick={() => onQty(r)} aria-label="Купить">
                {/* 88px канвы дают коробке визуальные ~58px из макета */}
                <DeckBox rarity={r} stage="idle" size={44} />
              </button>
              <div className="bp-shop-main">
                <button className="bp-shop-title" onClick={() => onQty(r)}>
                  <span className="nm">{RARITY_LABEL[r]} колода</span>
                  <span className="pr mono">
                    {entry.price.toLocaleString('ru-RU')} ₽
                    {starsOne !== null && <span className="bp-stars-alt"> · {starsOne} ⭐</span>}
                  </span>
                </button>
                <button className="bp-shop-drops" onClick={() => onPrizes(r)}>
                  {drops.map((d) => (
                    <span
                      key={d.id}
                      className="bp-shop-drop"
                      style={{ color: RARITY_COLOR[d.rarity], borderColor: rarityTint(d.rarity, 0.5), background: rarityTint(d.rarity, 0.14) }}
                    >
                      <SlotGlyph slot={d.slot} size={18} />
                    </span>
                  ))}
                  <span className="bp-shop-all">
                    Все призы
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="m9 5 7 7-7 7" />
                    </svg>
                  </span>
                </button>
                <button className="bp-link bp-shop-chances" onClick={onChances}>
                  Шансы
                </button>
                <div className={`disc${perBulk ? ' disc--deal' : ''}`}>
                  {perBulk
                    ? `от ${entry.bulkQty} шт — ${perBulk} ₽ за колоду`
                    : 'без скидки за количество'}
                </div>
              </div>
            </div>
          );
        })}
        <div className="bp-shop-note">
          В купленных колодах заморозки реже, чем в игровых, вечной нет
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── 6.10b количество ───────────────────────── */

function QtySheet({
  rarity,
  onClose,
  onOrdered,
}: {
  rarity: number;
  onClose: () => void;
  onOrdered: () => void;
}) {
  const shop = usePassShop();
  const order = usePassOrder();
  const stars = useStarsOrder(onOrdered);
  const starsAllowed = useFlag('pass_stars') && canPayStars;
  const [qty, setQty] = useState(1);
  const entry = shop.data?.shop[RKEY[rarity]];
  if (!entry) return null;
  const starsQty = starsAllowed ? starsTotal(entry, qty) : null;
  const busy = order.isPending || stars.pending;
  const max = shop.data?.maxOrderQty ?? 10;
  const bulk = entry.bulkQty && entry.bulkPrice && qty >= entry.bulkQty;
  const total = bulk
    ? Math.round((entry.bulkPrice! / entry.bulkQty!) * qty)
    : entry.price * qty;
  const full = entry.price * qty;
  const per = Math.round(total / qty);

  const word = qty === 1 ? 'колода' : qty < 5 ? 'колоды' : 'колод';
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-qty" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <div className="bp-qty-head">
          <span className="bp-qty-deck">
            <DeckBox rarity={rarity} stage="idle" size={38} />
          </span>
          <div className="bp-qty-head-main">
            <div className="nm">{RARITY_LABEL[rarity]} колода</div>
            <div className="sub">{entry.price.toLocaleString('ru-RU')} ₽ за колоду</div>
          </div>
        </div>
        <div className="bp-qty-row">
          <button className="bp-qty-btn" disabled={qty <= 1} onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Меньше">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M5 12h14" />
            </svg>
          </button>
          <span className="bp-qty-mid">
            <span className="bp-qty-num mono">{qty}</span>
            <span className="bp-qty-word">{word}</span>
          </span>
          <button className="bp-qty-btn" disabled={qty >= max} onClick={() => setQty((q) => Math.min(max, q + 1))} aria-label="Больше">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
        {entry.bulkQty && (
          <>
            <div className="bp-qty-scale">
              {/* макет: деление красится по своему номеру — до порога золото,
                  от порога зелёное; незаполненные после порога — бледно-зелёные */}
              {Array.from({ length: max }, (_, i) => (
                <i
                  key={i}
                  className={
                    i < qty
                      ? i + 1 >= (entry.bulkQty ?? Infinity)
                        ? 'green'
                        : 'gold'
                      : i + 1 >= (entry.bulkQty ?? Infinity)
                        ? 'pale'
                        : ''
                  }
                />
              ))}
            </div>
            <div className="bp-qty-marks mono">
              <span>1</span>
              <span className="from">{entry.bulkQty} — скидка</span>
              <span>{max}</span>
            </div>
          </>
        )}
        <div className="bp-qty-summary">
          <span className={`hint${bulk ? ' hint--deal' : ''}`}>
            {entry.bulkQty
              ? bulk
                ? `${per} ₽ за колоду · экономия ${(full - total).toLocaleString('ru-RU')} ₽`
                : `Ещё ${entry.bulkQty - qty} — и ${Math.round(entry.bulkPrice! / entry.bulkQty!)} ₽ за колоду`
              : 'Скидки за количество нет'}
          </span>
          <span className="sum mono">
            {bulk && <s>{full.toLocaleString('ru-RU')} ₽</s>}
            <b>{total.toLocaleString('ru-RU')} ₽</b>
          </span>
        </div>
        {/* Единый выбор способа оплаты (как на Pass+) */}
        <PayMethods
          methods={[
            ...(starsQty !== null
              ? [
                  {
                    key: 'stars',
                    label: `Оплатить ${starsQty.toLocaleString('ru-RU')} ⭐ в Telegram`,
                    primary: true,
                    disabled: busy,
                    onPick: () =>
                      void stars
                        .buy({ kind: 'decks', deckRarity: rarity, qty })
                        .catch(() => haptics.error()),
                  },
                ]
              : []),
            {
              key: 'desk',
              label: `Оплата у администратора — ${total.toLocaleString('ru-RU')} ₽`,
              primary: starsQty === null,
              disabled: busy,
              onPick: () => {
                haptics.tap();
                track('pass_order_create', { rarity, qty });
                void order
                  .mutateAsync({ kind: 'decks', deckRarity: rarity, qty })
                  .then(() => onOrdered())
                  .catch(() => haptics.error());
              },
            },
          ]}
        />
      </div>
    </div>
  );
}

/* ───────────────────────── 6.10c все призы ───────────────────────── */

function PrizesSheet({ rarity, onClose }: { rarity: number; onClose: () => void }) {
  const prizes = usePassPrizes(rarity);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-prizes" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <h3>Что может выпасть</h3>
        <div className="sheet-sub">
          {RARITY_LABEL[rarity]} колода · один предмет или заморозка
        </div>
        {(prizes.data?.groups ?? []).map((g) => (
          <div key={g.rarity} className="bp-prizes-group">
            <div className="bp-prizes-head">
              <RarityBadge rarity={g.rarity} male />
              <span className="mono pct rtx" style={rarityTextStyle(g.rarity)}>
                {g.pct}%
              </span>
            </div>
            <div className="bp-prizes-grid">
              {g.items.map((i) => (
                <div key={i.id} className="bp-prizes-item">
                  <span
                    className="bp-prizes-pv"
                    style={{ color: RARITY_COLOR[i.rarity], background: rarityTint(i.rarity, 0.14) }}
                  >
                    <SlotGlyph slot={i.slot} size={22} />
                  </span>
                  <span className="nm">{i.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {/* макет: одна плашка со снежинкой и общим текстом */}
        <div className="bp-prizes-note">
          <SnowIcon size={14} />
          <span>
            {(prizes.data?.freezePct ?? 0) > 0
              ? `Ещё ${prizes.data?.freezePct}% — заморозка баллов отбора. `
              : ''}
            Повтор предмета из гардероба даёт +50 XP Straddle Pass.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── 6.6 шансы ───────────────────────── */

/** «4,5%», ноль — «—» (формат макета). */
function pctLabel(v: number): string {
  return v > 0 ? `${String(v).replace('.', ',')}%` : '—';
}

function ChancesSheet({ onClose }: { onClose: () => void }) {
  const [source, setSource] = useState<'game' | 'bought'>('game');
  const [itemTab, setItemTab] = useState(1);
  const chances = usePassChances();
  if (!chances.data) return null;
  const c = chances.data.chances;
  const plus = chances.data.tier === 'plus';
  const freezeTable = (source === 'game' ? c.freezeGame : c.freezeBought) as Record<
    string,
    Record<string, number>
  >;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-chances" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <div className="bp-chances-head">
          <div>
            <h3>Шансы</h3>
            <div className="sheet-sub">
              {source === 'game' ? 'Игровая колода · для тебя сейчас' : 'Колода из магазина'}
            </div>
          </div>
          {/* пилюля статуса (макет 6.6) */}
          <span className={`bp-chances-pill${plus ? ' plus' : ''}`}>
            {plus ? 'Pass+' : 'Pass'}
          </span>
        </div>
        {(chances.data.guaranteeIn ?? 0) > 0 && (
          <div className="bp-chances-status mono">
            эпик гарантирован через {chances.data.guaranteeIn}
          </div>
        )}
        <div className="bp-src-toggle">
          <button className={source === 'game' ? 'on' : ''} onClick={() => setSource('game')}>
            Игровая
          </button>
          <button className={source === 'bought' ? 'on' : ''} onClick={() => setSource('bought')}>
            Купленная
          </button>
        </div>
        {source === 'game' && (
          <div className="bp-chances-block">
            <div className="bp-overline">РЕДКОСТЬ КОЛОДЫ</div>
            <div className="bp-chance-legend bp-chance-legend--top">
              <span />
              <span />
              <span className="mono">БЕЗ PASS+</span>
              <span className={`mono${plus ? ' on' : ''}`}>С PASS+</span>
            </div>
            {[0, 1, 2, 3].map((r) => (
              <div key={r} className={`bp-chance-row${(c.deckByTier.free[r] ?? 0) === 0 && (c.deckByTier.plus[r] ?? 0) === 0 ? ' bp-chance-row--zero' : ''}`}>
                {/* макет 6.6: ромб + имя нейтральным цветом текста */}
                <span className="nm">
                  <i className="rh" style={{ background: RARITY_COLOR[r] }} aria-hidden />
                  {RARITY_LABEL[r]}
                </span>
                <span className="bar">
                  <i style={{ width: `${(c.deckByTier.free[r] ?? 0) > 0 ? Math.max(2, c.deckByTier.free[r]) : 0}%`, background: RARITY_COLOR[r] }} />
                </span>
                <span className="pct mono">{pctLabel(c.deckByTier.free[r] ?? 0)}</span>
                <span className={`plus mono${plus ? ' on' : ''}`}>{pctLabel(c.deckByTier.plus[r] ?? 0)}</span>
              </div>
            ))}
            <div className="bp-chance-upnote">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1f9d6b" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
              Финальный стол повышает колоду на одну ступень
            </div>
          </div>
        )}
        <div className="bp-chances-block">
          <div className="bp-overline">ПРЕДМЕТ ВНУТРИ КОЛОДЫ</div>
          {/* Табы редкости колоды (6.6) */}
          <div className="bp-item-tabs">
            {[0, 1, 2, 3].map((r) => (
              <button
                key={r}
                className={itemTab === r ? 'on rtx' : ''}
                style={
                  itemTab === r
                    ? { ...rarityTextStyle(r), background: rarityTint(r, 0.15), borderColor: rarityTint(r, 0.6) }
                    : undefined
                }
                onClick={() => setItemTab(r)}
              >
                {RARITY_LABEL[r]}
              </button>
            ))}
          </div>
          {[0, 1, 2, 3].map((ir) => {
            const v = (c.itemByDeck[RKEY[itemTab]] ?? [])[ir] ?? 0;
            return (
              <div key={ir} className={`bp-chance-row${v === 0 ? ' bp-chance-row--zero' : ''}`}>
                <span className="nm rtx" style={rarityTextStyle(ir)}>
                  <i className="rh" style={{ background: RARITY_COLOR[ir] }} aria-hidden />
                  {RARITY_LABEL_M[ir]}
                </span>
                <span className="bar">
                  <i style={{ width: `${v > 0 ? Math.max(2, v) : 0}%`, background: RARITY_COLOR[ir] }} />
                </span>
                <span className="pct mono">{pctLabel(v)}</span>
              </div>
            );
          })}
        </div>
        {chances.data.freezesEnabled && !plus && source === 'game' && (
          // 6.6a без Pass+: пунктирная карточка с замком (макет)
          <div className="bp-chances-block">
            <div className="bp-overline">ЗАМОРОЗКИ</div>
            <div className="bp-chances-locked">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Заморозки выпадают только с Straddle Pass+. Колоды, открытые без Straddle Pass+, заморозок не содержат.
            </div>
          </div>
        )}
        {chances.data.freezesEnabled && (plus || source === 'bought') && (
          <div className="bp-chances-block">
            <div className="bp-overline">ЗАМОРОЗКИ (ТОЛЬКО STRADDLE PASS+)</div>
            {source === 'bought' ? (
              // Формат спеки для купленных: На 3 игры / На неделю / Всего
              (() => {
                const sum = (k: string) =>
                  Object.values(freezeTable).reduce((a, t) => a + (t[k] ?? 0), 0);
                const g3 = sum('game3');
                const wk = sum('week');
                const g1 = sum('game1');
                return (
                  <div className="bp-freeze-cols mono">
                    <span>
                      <b>{g3}%</b>На 3 игры
                    </span>
                    <span>
                      <b>{wk}%</b>На неделю
                    </span>
                    <span>
                      <b>{g1 + g3 + wk}%</b>Всего
                    </span>
                  </div>
                );
              })()
            ) : (
              Object.entries(freezeTable).map(([deckR, table]) => (
                <div key={deckR} className="bp-chance-row">
                  <span className="nm rtx" style={rarityTextStyle(RKEY.indexOf(deckR as (typeof RKEY)[number]))}>
                    {RARITY_LABEL[RKEY.indexOf(deckR as (typeof RKEY)[number])]}
                  </span>
                  <span className="dist mono">
                    {Object.entries(table)
                      .map(([k, pct]) => `${FREEZE_SHORT[k] ?? k} ${pct}%`)
                      .join(' · ')}
                  </span>
                </div>
              ))
            )}
            {source === 'bought' && (
              <div className="bp-shop-note">Если заморозка не выпала — в колоде косметика</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── заказ: код + QR + таймер ───────────────────────── */

export function OrderSheet({ onClose }: { onClose: () => void }) {
  const active = usePassActiveOrder(true, true);
  const invoice = usePassOrderInvoice();
  const cancel = usePassCancelOrder();
  const [qr, setQr] = useState<string | null>(null);
  const order = active.data?.order ?? null;

  useEffect(() => {
    if (!order || order.provider === 'stars') return;
    void QRCode.toDataURL(order.code, { margin: 1, width: 220 }).then(setQr);
  }, [order?.code, order?.provider]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (order?.status === 'paid') haptics.success();
  }, [order?.status]);

  if (!order) return null;
  const left = Math.max(0, Math.floor((new Date(order.expiresAt).getTime() - Date.now()) / 60000));
  const isStars = order.provider === 'stars';

  const payStars = () => {
    haptics.tap();
    void invoice
      .mutateAsync(order.code)
      .then(({ link }) => openInvoice(link, () => void active.refetch()))
      .catch(() => haptics.error());
  };
  const cancelOrder = () => {
    haptics.tap();
    void cancel
      .mutateAsync(order.code)
      .then(() => onClose())
      .catch(() => haptics.error());
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-order" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        {order.status === 'paid' ? (
          <>
            <h3>Оплачено ✓</h3>
            <div className="sheet-sub">
              {order.kind === 'plus'
                ? 'Straddle Pass+ активирован'
                : `${order.qty} колод уже в инвентаре`}
            </div>
            <button className="btn" style={{ width: '100%', marginTop: 12 }} onClick={onClose}>
              Отлично
            </button>
          </>
        ) : isStars ? (
          <>
            <h3>Оплата звёздами</h3>
            <div className="sheet-sub">
              {`${order.qty} × ${RARITY_LABEL[order.deckRarity ?? 1]} колода`} ·{' '}
              <b className="mono">{order.starsPrice ?? '—'} ⭐</b>
            </div>
            <div className="bp-order-timer mono" style={{ marginTop: 10 }}>
              {order.status === 'expired'
                ? 'Заказ истёк — создай новый'
                : `Заказ действует ещё ~${left} мин`}
            </div>
            <button
              className="btn"
              style={{ width: '100%', marginTop: 12 }}
              disabled={invoice.isPending}
              onClick={payStars}
            >
              Оплатить {order.starsPrice ?? ''} ⭐
            </button>
            <button
              className="btn-secondary bp-btn-secondary"
              style={{ width: '100%', marginTop: 8 }}
              disabled={cancel.isPending}
              onClick={cancelOrder}
            >
              Отменить заказ
            </button>
          </>
        ) : (
          <>
            <h3>Покажи код на стойке</h3>
            <div className="sheet-sub">
              {order.kind === 'plus'
                ? 'Straddle Pass+'
                : `${order.qty} × ${RARITY_LABEL[order.deckRarity ?? 1]} колода`}{' '}
              · <b className="mono">{order.price.toLocaleString('ru-RU')} ₽</b>
            </div>
            <div className="bp-order-code mono">{order.code}</div>
            {qr && <img className="bp-order-qr" src={qr} alt="QR" />}
            <div className="bp-order-timer mono">
              {order.status === 'expired' ? 'Код истёк — создай заказ заново' : `Код действует ещё ~${left} мин`}
            </div>
            <button
              className="bp-link"
              style={{ marginTop: 10 }}
              disabled={cancel.isPending}
              onClick={cancelOrder}
            >
              Отменить заказ
            </button>
          </>
        )}
      </div>
    </div>
  );
}
